import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingBag, Store, Bike, ArrowLeft, MapPin, Clock, ShieldCheck, Star,
  Bell, Tag, Zap, Check, Quote, Utensils, ShoppingBasket, Cake, Carrot,
  Pill, Smartphone, Shirt, Croissant, Route, Wallet,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import ColorThemeSwitcher from "@/components/ColorThemeSwitcher";
import BrandLogo from "@/components/BrandLogo";

const HERO = "https://images.unsplash.com/photo-1550547660-d9450f859349?crop=entropy&cs=srgb&fm=jpg&w=1600&q=55&ixlib=rb-4.1.0";
const DRIVER_IMG = "https://images.unsplash.com/photo-1617347454431-f49d7ff5c3b1?crop=entropy&cs=srgb&fm=jpg&w=900&q=55&ixlib=rb-4.1.0";
const GROCERIES = "https://images.pexels.com/photos/26309839/pexels-photo-26309839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BURGER_SHOP = "https://images.pexels.com/photos/8214342/pexels-photo-8214342.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.45 },
};

const categories = [
  { icon: Utensils, name: "مطاعم" }, { icon: ShoppingBasket, name: "بقالة" },
  { icon: Carrot, name: "خضار وفواكه" }, { icon: Cake, name: "حلويات" },
  { icon: Croissant, name: "مخابز" }, { icon: Pill, name: "صيدلية" },
  { icon: Smartphone, name: "إلكترونيات" }, { icon: Shirt, name: "ملابس" },
];

const steps = [
  { n: "1", title: "اختر متجرك", desc: "تصفّح مئات المنتجات من متاجر مدينتك الموثوقة واملأ سلتك." },
  { n: "2", title: "أكمل الطلب", desc: "أدخل عنوانك وادفع نقداً عند الاستلام — بلا تعقيد ولا بطاقات." },
  { n: "3", title: "تابع حتى بابك", desc: "شاهد سائقك يقترب على الخريطة لحظة بلحظة حتى يصل إليك." },
];

const features = [
  { icon: Route, title: "تتبّع مباشر على الخريطة", desc: "اعرف أين طلبك بالضبط ومتى يصل، بمسار حقيقي يتبع الشوارع." },
  { icon: Bell, title: "إشعارات فورية", desc: "تنبيهات لحظية تصلك حتى والتطبيق مغلق عند كل تحديث لطلبك." },
  { icon: ShieldCheck, title: "تقييمات موثوقة", desc: "آراء حقيقية من مشترين استلموا المنتج فعلاً — لا مجاملات." },
  { icon: Wallet, title: "دفع عند الاستلام", desc: "ادفع نقداً حين يصلك طلبك، آمن وبسيط ومناسب للجميع." },
  { icon: Tag, title: "عروض وخصومات", desc: "أفضل التخفيضات من متاجر مدينتك في مكان واحد كل يوم." },
  { icon: Zap, title: "توصيل سريع", desc: "شبكة سائقين محليين توصل طلبك في أقصر وقت ممكن." },
];

const testimonials = [
  { name: "أبو محمد", role: "مشتري", text: "صرت أطلب كل احتياجات البيت من جوالي، يوصل بسرعة وبالسعر نفسه. وفّر علي وقت كبير." },
  { name: "سمر", role: "صاحبة متجر", text: "زادت مبيعاتي بعد ما نزّلت منتجاتي على التطبيق ووصلت لزبائن جدد بمدينتي." },
  { name: "خالد", role: "سائق", text: "أشتغل بالأوقات يلي تناسبني وأستلم أرباحي عن كل توصيلة. دخل إضافي ممتاز." },
];

const faqs = [
  { q: "ما هي مناطق التوصيل؟", a: "نخدم حالياً مدينة كفرنبل وريفها القريب، ونتوسّع تدريجياً لمناطق جديدة." },
  { q: "كيف أدفع؟", a: "الدفع نقداً عند استلام الطلب. تدفع للسائق مباشرةً عند وصوله إليك." },
  { q: "كيف أسجّل متجري؟", a: "أنشئ حساب بائع خلال دقيقة، أضف متجرك ومنتجاتك بالصور، وابدأ باستقبال الطلبات فوراً." },
  { q: "كيف أعمل كسائق؟", a: "سجّل كسائق، وستظهر لك الطلبات الجاهزة المتاحة قربك لتقبلها وتربح عن كل توصيلة." },
  { q: "هل التقييمات حقيقية؟", a: "نعم، لا يمكن التقييم إلا لمن اشترى المنتج واستلمه فعلاً، ولا يمكن للمتاجر حذف أي تقييم." },
];

const Logo = () => (
  <BrandLogo className="h-12 w-[140px] object-contain" />
);

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="landing-page">
      {/* Nav */}
      <header style={{ paddingTop: scrolled ? "calc(env(safe-area-inset-top) + 0.5rem)" : "calc(env(safe-area-inset-top) + 1rem)" }} className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-card/95 border-b border-border py-2 shadow-card" : "py-4"}`}>
        <div className="max-w-6xl mx-auto px-5 flex items-center justify-between">
          <Logo />
          <nav className={`hidden lg:flex items-center gap-7 text-sm font-semibold ${scrolled ? "text-foreground" : "text-white/90"}`}>
            <button type="button" onClick={() => navigate("/about")} className="hover:text-primary transition-colors">معلومات البرنامج</button>
            <a href="#how" className="hover:text-primary transition-colors">كيف يعمل</a>
            <a href="#features" className="hover:text-primary transition-colors">المميزات</a>
            <a href="#roles" className="hover:text-primary transition-colors">انضم إلينا</a>
            <a href="#faq" className="hover:text-primary transition-colors">الأسئلة</a>
          </nav>
          <div className="flex items-center gap-2">
            <button data-testid="header-login-btn" onClick={() => navigate("/auth?mode=login")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${scrolled ? "text-foreground hover:bg-muted" : "text-white hover:bg-white/15"}`}>
              دخول
            </button>
            <button data-testid="header-start-btn" onClick={() => navigate("/auth")}
              className={`px-5 py-2 rounded-full text-sm font-bold hover:-translate-y-0.5 transition-all ${scrolled ? "bg-primary text-primary-foreground shadow-cta hover:brightness-105" : "bg-white text-primary shadow-xl hover:bg-white/90"}`}>
              ابدأ الآن
            </button>
            <ColorThemeSwitcher />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-[82svh] flex items-center sm:min-h-[92vh]">
        <div className="absolute inset-0">
          <img src={HERO} alt="بيب بيب" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand via-brand/35 to-transparent" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-5 pt-24 pb-12 sm:pt-32 sm:pb-20 grid lg:grid-cols-2 gap-8 sm:gap-12 items-center w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-1.5 rounded-full text-xs mb-6 border border-white/20">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> نوصّل في كفرنبل وريفها
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.4]">
              جوعان أو ناقصك شي؟<br /><span className="text-white underline decoration-white/25 decoration-8 underline-offset-4">بيب بيب</span> يوصّلك
            </h1>
            <p className="text-white/75 text-base sm:text-lg mt-6 leading-relaxed max-w-xl">
              اطلب من مطاعمك وبقالياتك المفضّلة بضغطة زر، وتابع سائقك على الخريطة حتى يصل لبابك. سريع، موثوق، ومحلي.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button data-testid="hero-buyer-btn" onClick={() => navigate("/auth?role=buyer")}
                className="inline-flex items-center justify-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-bold hover:bg-white/90 hover:-translate-y-0.5 active:scale-95 transition-all shadow-xl">
                اطلب الآن <ArrowLeft className="w-5 h-5" />
              </button>
              <button data-testid="hero-seller-btn" onClick={() => navigate("/auth?role=seller")}
                className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full font-bold border border-white/25 hover:bg-white/15 transition-colors">
                <Store className="w-5 h-5" /> سجّل متجرك
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-white/70 text-sm">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-white" /> بدون رسوم اشتراك</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-white" /> دفع عند الاستلام</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-white" /> تتبّع مباشر</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25, duration: 0.5 }}
            className="hidden lg:block relative h-[420px]">
            <div className="absolute top-6 right-4 bg-white rounded-2xl p-5 w-60 shadow-2xl">
              <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-full bg-primary/15 grid place-items-center"><Bike className="w-5 h-5 text-primary" /></div><span className="font-bold text-sm">سائقك في الطريق</span></div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full w-2/3 bg-primary" /></div>
              <p className="text-xs mt-2 text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> يصل خلال 8 دقائق</p>
            </div>
            <div className="absolute bottom-12 left-2 bg-white rounded-2xl p-5 w-56 shadow-2xl">
              <div className="flex items-center gap-0.5 text-rating">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-rating" />)}</div>
              <p className="font-bold mt-2 text-sm">تقييمات موثوقة</p>
              <p className="text-xs text-muted-foreground mt-1">من مشترين حقيقيين فقط</p>
            </div>
            <div className="absolute top-44 left-20 bg-white text-primary rounded-2xl px-5 py-4 shadow-2xl">
              <div className="font-num font-extrabold text-2xl">30+</div>
              <p className="text-xs text-muted-foreground">متجر محلي</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-brand text-white">
        <div className="max-w-6xl mx-auto px-5 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[["30+", "متجر محلي"], ["15 دقيقة", "متوسط التوصيل"], ["1000+", "طلب مكتمل"], ["4.8", "تقييم المستخدمين"]].map(([v, l]) => (
            <div key={l}>
              <div className="font-num font-extrabold text-2xl md:text-3xl text-white">{v}</div>
              <div className="text-white/60 text-sm mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How */}
      <section id="how" className="max-w-6xl mx-auto px-5 py-12 sm:py-20 md:py-24">
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <span className="text-primary font-bold text-sm">بخطوات بسيطة</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">كيف يعمل بيب بيب؟</h2>
          <p className="text-muted-foreground mt-3">من اختيار المنتج حتى وصوله لبابك، تجربة سلسة بثلاث خطوات.</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-3 sm:gap-6 mt-7 sm:mt-12">
          {steps.map((s, i) => (
            <motion.div key={s.n} {...fade} transition={{ ...fade.transition, delay: i * 0.1 }}
              className="relative bg-card rounded-2xl p-5 sm:p-8 border border-border">
              <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center font-num font-extrabold text-xl sm:text-2xl shadow-cta">{s.n}</div>
              <h3 className="font-display font-bold text-lg sm:text-xl mt-3 sm:mt-5">{s.title}</h3>
              <p className="text-muted-foreground mt-2 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="bg-accent/40 border-y border-border">
        <div className="max-w-6xl mx-auto px-5 py-10 sm:py-16">
          <motion.h2 {...fade} className="font-display font-bold text-2xl md:text-3xl text-center mb-10">اطلب من كل الأصناف</motion.h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {categories.map((c, i) => (
              <motion.button key={c.name} {...fade} transition={{ ...fade.transition, delay: i * 0.04 }}
                onClick={() => navigate("/auth?role=buyer")}
                className="group bg-card rounded-2xl p-6 border border-border flex flex-col items-center gap-3 hover:-translate-y-1 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 grid place-items-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <c.icon className="w-7 h-7" />
                </div>
                <span className="font-semibold text-sm">{c.name}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-12 sm:py-20 md:py-24">
        <motion.div {...fade} className="text-center max-w-2xl mx-auto">
          <span className="text-primary font-bold text-sm">لماذا بيب بيب</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-2">مميزات تجعل تجربتك أفضل</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mt-7 sm:mt-12">
          {features.map((f, i) => (
            <motion.div key={f.title} {...fade} transition={{ ...fade.transition, delay: i * 0.06 }}
              className="bg-card rounded-2xl p-5 sm:p-7 border border-border hover:border-primary/40 transition-colors">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary/10 grid place-items-center text-primary">
                <f.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-base sm:text-lg mt-3 sm:mt-5">{f.title}</h3>
              <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Roles */}
      <section id="roles" className="bg-brand text-white">
        <div className="max-w-6xl mx-auto px-5 py-12 sm:py-20 md:py-24">
          <motion.div {...fade} className="text-center max-w-2xl mx-auto">
            <h2 className="font-display font-bold text-3xl md:text-4xl">مكانك في بيب بيب</h2>
            <p className="text-white/60 mt-3">سواء كنت مشترياً أو صاحب متجر أو سائقاً، لدينا ما يناسبك.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-3 sm:gap-6 mt-7 sm:mt-12">
            {[
              { img: GROCERIES, icon: ShoppingBag, title: "مشتري", desc: "اطلب كل احتياجاتك واستلمها بأسرع وقت عند بابك.", role: "buyer", cta: "ابدأ الطلب" },
              { img: BURGER_SHOP, icon: Store, title: "صاحب متجر", desc: "اعرض منتجاتك، وصل لزبائن جدد، ونمِّ مبيعاتك.", role: "seller", cta: "سجّل متجرك" },
              { img: DRIVER_IMG, icon: Bike, title: "سائق", desc: "اعمل بأوقاتك واربح دخلاً إضافياً عن كل توصيلة.", role: "driver", cta: "انضم كسائق" },
            ].map((r, i) => (
              <motion.div key={r.role} {...fade} transition={{ ...fade.transition, delay: i * 0.1 }}
                className="bg-brand-soft rounded-2xl overflow-hidden border border-white/10 flex flex-col">
                <div className="h-36 sm:h-44 overflow-hidden relative">
                  <img src={r.img} alt={r.title} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-soft to-transparent" />
                  <div className="absolute bottom-4 right-5 flex items-center gap-2">
                    <div className="w-10 h-10 rounded-xl bg-white grid place-items-center text-primary"><r.icon className="w-5 h-5" /></div>
                    <span className="font-display font-bold text-lg">{r.title}</span>
                  </div>
                </div>
                <div className="p-4 sm:p-6 flex flex-col flex-1">
                  <p className="text-white/70 text-sm leading-relaxed flex-1">{r.desc}</p>
                  <button data-testid={`role-card-${r.role}`} onClick={() => navigate(`/auth?role=${r.role}`)}
                    className="mt-5 inline-flex items-center justify-center gap-2 bg-white text-primary py-3 rounded-full font-bold hover:bg-white/90 hover:-translate-y-0.5 transition-all shadow-xl">
                    {r.cta} <ArrowLeft className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value split */}
      <section className="max-w-6xl mx-auto px-5 py-20 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fade} className="rounded-2xl overflow-hidden border border-border h-80 lg:h-[420px]">
          <img src={GROCERIES} alt="منتجات طازجة" loading="lazy" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div {...fade}>
          <span className="text-primary font-bold text-sm">تجربة تسوّق ممتعة</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 leading-snug">منتجات بصور واقعية وتفاصيل واضحة</h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">
            كل منتج يُعرض بصورة عالية الجودة، وصف كامل، سعر شفّاف، وتقييمات موثوقة. تسوّق بثقة وأنت تعرف تماماً ماذا ستستلم.
          </p>
          <ul className="mt-6 space-y-3">
            {["صور منتجات احترافية بمقاس مثالي", "تقييمات من مشترين حقيقيين فقط", "عروض وخصومات محدّثة يومياً", "سلة ودفع بخطوات بسيطة"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/15 grid place-items-center text-primary shrink-0"><Check className="w-4 h-4" /></div>
                <span className="text-sm font-medium">{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </section>

      {/* Testimonials */}
      <section className="bg-accent/40 border-y border-border">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <motion.h2 {...fade} className="font-display font-bold text-3xl md:text-4xl text-center mb-12">ماذا يقول مستخدمونا؟</motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} {...fade} transition={{ ...fade.transition, delay: i * 0.1 }}
                className="bg-card rounded-2xl p-7 border border-border">
                <Quote className="w-8 h-8 text-primary" />
                <p className="text-muted-foreground mt-4 leading-relaxed">"{t.text}"</p>
                <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border">
                  <div className="w-11 h-11 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-bold">{t.name[0]}</div>
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-5 py-20 md:py-24">
        <motion.h2 {...fade} className="font-display font-bold text-3xl md:text-4xl text-center mb-10">الأسئلة الشائعة</motion.h2>
        <motion.div {...fade}>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`} className="bg-card border border-border rounded-2xl px-5">
                <AccordionTrigger className="text-start font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 pb-24">
        <motion.div {...fade} className="rounded-3xl overflow-hidden bg-primary text-primary-foreground p-10 shadow-cta md:p-16 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-display font-extrabold text-3xl md:text-5xl leading-tight">جاهز تبدأ؟</h2>
            <p className="text-white/85 mt-4 text-lg">انضم لآلاف المستخدمين الذين يوفّرون وقتهم مع بيب بيب.</p>
            <button data-testid="cta-start-btn" onClick={() => navigate("/auth")}
              className="mt-8 inline-flex items-center justify-center gap-2 bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:-translate-y-0.5 active:scale-95 transition-transform">
              أنشئ حسابك مجاناً <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-5 py-12 grid sm:grid-cols-2 md:grid-cols-4 gap-8">
          <div className="sm:col-span-2 md:col-span-1">
            <Logo />
            <p className="text-muted-foreground text-sm mt-4 leading-relaxed">منصّة التوصيل المحلية التي تجمع متاجر ومشتري وسائقي مدينتك في مكان واحد.</p>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3">التطبيق</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="#how" className="hover:text-primary">كيف يعمل</a></li>
              <li><a href="#features" className="hover:text-primary">المميزات</a></li>
              <li><a href="#faq" className="hover:text-primary">الأسئلة الشائعة</a></li>
              <li><button type="button" onClick={() => navigate("/about")} className="hover:text-primary">معلومات البرنامج</button></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3">انضم إلينا</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><button onClick={() => navigate("/auth?role=buyer")} className="hover:text-primary">كمشتري</button></li>
              <li><button onClick={() => navigate("/auth?role=seller")} className="hover:text-primary">كصاحب متجر</button></li>
              <li><button onClick={() => navigate("/auth?role=driver")} className="hover:text-primary">كسائق</button></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3">تواصل</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4" /> كفرنبل، سوريا</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border py-5 text-center text-sm text-muted-foreground">
          بيب بيب © 2026 — جميع الحقوق محفوظة
        </div>
      </footer>
    </div>
  );
}
